import React, { useMemo } from "react";
import {
  List,
  Filter,
  Datagrid,
  ReferenceInput,
  SelectInput,
  EditButton,
  TextInput,
  TextField,
  NumberField,
  FunctionField,
  useDataProvider,
  useGetList,
  DataProviderContext,
} from "react-admin";
import * as XLSX from "xlsx";

const CategoriesTitle = () => {
  return <span>Listado de Categorías</span>;
};

const CategoriesFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Buscar nombre" source="name" alwaysOn />
    <ReferenceInput
      label="Categoría padre"
      source="parent"
      reference="categories"
      allowEmpty
      sort={{ field: "name", order: "ASC" }}
      filter={{ parent: null }}
    >
      <SelectInput optionText="name" label="Categoría padre" />
    </ReferenceInput>
  </Filter>
);

const performCategoriesExport = async (records, dataProvider) => {
  const { data: allCategories } = await dataProvider.getList("categories", {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "id", order: "ASC" },
    filter: {},
  });

  const categoryMap = new Map(allCategories.map((cat) => [cat.id, cat.name]));

  const exportData = records.map((record) => ({
    ID: record.id,
    Nombre: record.name,
    "Categoría Padre": record.parent
      ? categoryMap.get(record.parent) || record.parent
      : "-",
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  const colWidths = Object.keys(exportData[0]).map((h) => ({
    wch: Math.max(h.length, 15),
  }));
  ws["!cols"] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, "Categorías");
  XLSX.writeFile(
    wb,
    `categorias-${new Date().toISOString().split("T")[0]}.xlsx`
  );
};

const CategoriesListView = (props) => {
  const dataProvider = useDataProvider();

  const { data: parentCategories, loading: loadingParentCategories } =
    useGetList("categories", {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "name", order: "ASC" },
      filter: { parent: null },
    });

  const parentCategoryMap = React.useMemo(() => {
    if (loadingParentCategories || !parentCategories) return new Map();
    return new Map(parentCategories.map((cat) => [cat.id, cat.name]));
  }, [parentCategories, loadingParentCategories]);

  return (
    <List
      filters={<CategoriesFilter />}
      title={<CategoriesTitle />}
      {...props}
      sort={{ field: "id", order: "ASC" }}
      exporter={(records) => performCategoriesExport(records, dataProvider)}
    >
      <Datagrid rowClick="edit">
        <NumberField source="id" label="ID" />
        <TextField source="name" label="Nombre" />
        <FunctionField
          label="Categoría Padre"
          render={(record) => {
            if (record.parent && parentCategoryMap.has(record.parent)) {
              return parentCategoryMap.get(record.parent);
            }
            return " - ";
          }}
        />
        <EditButton />
      </Datagrid>
    </List>
  );
};

export const CategoriesList = (props) => {
  const dataProvider = useDataProvider();

  const transformedDataProvider = useMemo(
    () => ({
      ...dataProvider,
      getList: (resource, params) => {
        if (resource === "categories" && params.filter?.name) {
          const { name, ...restFilter } = params.filter;
          const newFilter = {
            ...restFilter,
            name: { contains: name },
          };
          return dataProvider.getList(resource, {
            ...params,
            filter: newFilter,
          });
        }
        return dataProvider.getList(resource, params);
      },
    }),
    [dataProvider]
  );

  return (
    <DataProviderContext.Provider value={transformedDataProvider}>
      <CategoriesListView {...props} />
    </DataProviderContext.Provider>
  );
};
